import React, { useState } from 'react';
import { useOptimizedImage, UseOptimizedImageOptions } from '@/hooks/useOptimizedImage';
import { motion, AnimatePresence } from 'framer-motion';

// Omit the conflicting properties from HTMLAttributes that are also in UseOptimizedImageOptions
type OptimizedImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height'> & UseOptimizedImageOptions & {
  src: string | null | undefined;
  alt?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  className?: string;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  aspectRatio?: '1:1' | '4:3' | '16:9' | '3:2' | '2:3' | 'auto';
  animation?: 'fade' | 'zoom' | 'slide' | 'none';
};

/**
 * A component for optimized and elegant image display with fallback handling
 */
export function OptimizedImage({
  src,
  alt,
  className,
  width,
  height,
  quality,
  format,
  placeholder = '/placeholder.svg',
  objectFit = 'cover',
  rounded = 'md',
  aspectRatio = 'auto',
  animation = 'fade',
  ...rest
}: OptimizedImageProps) {
  const {
    src: optimizedSrc,
    isLoading
  } = useOptimizedImage(src || placeholder, {
    width,
    height,
    quality,
    format,
    placeholder
  });
  const [loaded, setLoaded] = useState(false);

  // Map rounded values to tailwind classes
  const roundedMap = {
    'none': '',
    'sm': 'rounded-sm',
    'md': 'rounded-md',
    'lg': 'rounded-lg',
    'xl': 'rounded-xl',
    'full': 'rounded-full'
  };

  // Map objectFit values to tailwind classes
  const objectFitMap = {
    'cover': 'object-cover',
    'contain': 'object-contain',
    'fill': 'object-fill',
    'none': 'object-none',
    'scale-down': 'object-scale-down'
  };

  // Map aspectRatio values to tailwind classes
  const aspectRatioMap = {
    '1:1': 'aspect-square',
    '4:3': 'aspect-[4/3]',
    '16:9': 'aspect-[16/9]',
    '3:2': 'aspect-[3/2]',
    '2:3': 'aspect-[2/3]',
    'auto': ''
  };

  // Animation variants for image loading
  const animations = {
    fade: {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.3
      }
    },
    zoom: {
      initial: {
        opacity: 0,
        scale: 1.1
      },
      animate: {
        opacity: 1,
        scale: 1
      },
      transition: {
        duration: 0.4
      }
    },
    slide: {
      initial: {
        opacity: 0,
        y: 10
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.3
      }
    },
    none: {
      initial: {},
      animate: {},
      transition: {}
    }
  };
  const selectedAnimation = animations[animation];
  const handleLoad = () => {
    setLoaded(true);
    if (rest.onLoad) {
      // Fix: Create a proper synthetic event instead of using a raw Event
      rest.onLoad({} as React.SyntheticEvent<HTMLImageElement, Event>);
    }
  };
  return <div className={`${aspectRatioMap[aspectRatio]} ${roundedMap[rounded]} overflow-hidden relative`}>
      <AnimatePresence>
        {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-transparent mx-[27px]">
            <div className="w-8 h-8 border-2 border-hivebubble-coral/30 border-t-hivebubble-coral rounded-full animate-spin" />
          </div>}
      </AnimatePresence>
      
      <motion.img src={optimizedSrc} alt={alt || ''} className={`${className || ''} ${loaded ? 'opacity-100' : 'opacity-0'} ${objectFitMap[objectFit]} ${roundedMap[rounded]} w-full h-full transition-opacity duration-300`} width={width} height={height} loading="lazy" onLoad={handleLoad} initial={selectedAnimation.initial} animate={loaded ? selectedAnimation.animate : selectedAnimation.initial} transition={selectedAnimation.transition}
    // Remove onDrag to fix the type error
    {...Object.fromEntries(Object.entries(rest).filter(([key]) => key !== 'onDrag'))} />
    </div>;
}